import { fetchAuthSession, getCurrentUser, signOut } from 'aws-amplify/auth';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from 'react';

/**
 * Web認証ユーザー情報
 */
export interface WebAuthUser {
  /** ユーザーID（sub） */
  userId: string;
  /** メールアドレス */
  email?: string;
  /** グループ（将来拡張用） */
  groups?: string[];
}

/**
 * Web認証状態
 */
export interface WebAuthState {
  /** 認証済みかどうか */
  isAuthenticated: boolean;
  /** ローディング中かどうか */
  isLoading: boolean;
  /** ユーザー情報 */
  user: WebAuthUser | null;
  /** エラーメッセージ */
  error: string | null;
}

/**
 * Web認証コンテキストの値
 *
 * サインインの手順（MFA を含む）は `useSignInFlow` が担う。完了後に `checkAuth` で認証状態を更新する。
 */
export interface WebAuthContextValue extends WebAuthState {
  /** サインアウト */
  signOut: () => Promise<void>;
  /** 認証状態を再確認する。認証済みなら true */
  checkAuth: () => Promise<boolean>;
}

const WebAuthContext = createContext<WebAuthContextValue | null>(null);

/**
 * JWTトークンからユーザー情報を抽出
 */
function parseUserFromToken(idToken: string): WebAuthUser | null {
  try {
    const parts = idToken.split('.');
    if (parts.length !== 3 || !parts[1]) return null;

    const payload = JSON.parse(atob(parts[1]));
    const user: WebAuthUser = {
      userId: payload.sub,
      groups: payload['cognito:groups'] || [],
    };
    if (payload.email) {
      user.email = payload.email;
    }
    return user;
  } catch {
    return null;
  }
}

/**
 * Web認証コンテキストプロバイダー
 */
export function WebAuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<WebAuthState>({
    isAuthenticated: false,
    isLoading: true,
    user: null,
    error: null,
  });

  /**
   * 認証状態を確認
   */
  const checkAuth = useCallback(async (): Promise<boolean> => {
    try {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      // 現在のユーザーを取得
      const currentUser = await getCurrentUser();

      // セッション（トークン）を取得
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) {
        setState({
          isAuthenticated: false,
          isLoading: false,
          user: null,
          error: null,
        });
        return false;
      }

      // トークンからユーザー情報を取得
      const user = parseUserFromToken(idToken);

      let finalUser = user;
      if (!finalUser) {
        const fallbackUser: WebAuthUser = {
          userId: currentUser.userId,
        };
        const loginEmail = currentUser.signInDetails?.loginId;
        if (loginEmail) {
          fallbackUser.email = loginEmail;
        }
        finalUser = fallbackUser;
      }
      setState({
        isAuthenticated: true,
        isLoading: false,
        user: finalUser,
        error: null,
      });
      return true;
    } catch {
      // 未認証の場合
      setState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        error: null,
      });
      return false;
    }
  }, []);

  /**
   * サインアウト
   */
  const handleSignOut = useCallback(async () => {
    try {
      setState((prev) => ({ ...prev, isLoading: true }));
      await signOut();
      setState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        error: null,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'サインアウトに失敗しました';
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: message,
      }));
    }
  }, []);

  // 初期化時に認証状態を確認
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const value: WebAuthContextValue = {
    ...state,
    signOut: handleSignOut,
    checkAuth,
  };

  return <WebAuthContext.Provider value={value}>{children}</WebAuthContext.Provider>;
}

/**
 * Web認証コンテキストを使用するフック
 */
export function useWebAuth(): WebAuthContextValue {
  const context = useContext(WebAuthContext);
  if (!context) {
    throw new Error('useWebAuth must be used within a WebAuthProvider');
  }
  return context;
}
